
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createHoliday } from "../redux/actions/holidayActions";
import { 
  FiPlus, 
  FiCheck, 
  FiArrowLeft, 
  FiCalendar, 
  FiType, 
  FiFileText, 
  FiAlertCircle,
  FiMapPin
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateHoliday = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { creating, error, success } = useSelector((state) => state.holiday);

  const [formData, setFormData] = useState({
    holiday_name: "",
    holiday_date: "",
    description: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.holiday_name.trim() || !formData.holiday_date || !formData.description.trim()) return;

    // Create clean payload
    const payload = {
      holiday_name: formData.holiday_name.trim(),
      holiday_date: formData.holiday_date,
      description: formData.description.trim()
    };

    dispatch(createHoliday(payload));
  };

  // Reset form when creation is successful
  React.useEffect(() => {
    if (success && !creating) {
      setFormData({
        holiday_name: "",
        holiday_date: "",
        description: ""
      });
    }
  }, [success, creating]);

  // Set minimum date to today
  const today = new Date().toISOString().split('T')[0];

  const isFormValid = formData.holiday_name.trim() !== "" && 
                     formData.holiday_date !== "" && 
                     formData.description.trim() !== "";

  if (creating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Creating Holiday...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/view-holiday")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Holidays
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiCalendar className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Holiday
                </h1>
                <p className="text-blue-100">
                  Add a new holiday to the organization calendar
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      Holiday created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      The new holiday has been added to the calendar.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiAlertCircle className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {typeof error === "string" ? error : error?.message || "Something went wrong"}
                    </p>
                    <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                      Please check your input and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                {/* Holiday Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiType className="text-sm" />
                      Holiday Name <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.holiday_name}
                    onChange={(e) => handleChange("holiday_name", e.target.value)}
                    placeholder="Enter holiday name (e.g., New Year's Day, Diwali, Christmas, etc.)"
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Official name of the holiday as it will appear on the calendar
                  </p>
                </div>

                {/* Holiday Date */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiCalendar className="text-sm" />
                      Holiday Date <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <input
                    type="date"
                    value={formData.holiday_date}
                    onChange={(e) => handleChange("holiday_date", e.target.value)}
                    min={today}
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select the date when this holiday occurs
                  </p>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-sm" />
                      Description <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Enter holiday description, significance, and any special instructions..."
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    required
                    disabled={creating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Provide context and information about this holiday
                  </p>
                </div>
              </div>

              {/* Preview Card */}
              {formData.holiday_name && formData.holiday_date && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                    <FiMapPin className="text-sm" />
                    Holiday Preview
                  </h4>
                  <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <p><span className="font-medium">Name:</span> {formData.holiday_name}</p>
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      {formData.holiday_date ? new Date(formData.holiday_date).toLocaleDateString("en-IN", {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : "-"}
                    </p>
                    {formData.description && (
                      <p><span className="font-medium">Description:</span> {formData.description}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      holiday_name: "",
                      holiday_date: "",
                      description: ""
                    });
                  }}
                  disabled={creating}
                  className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Form
                </button>
                
                <button
                  type="submit"
                  disabled={creating || !isFormValid}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {creating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Holiday...
                    </>
                  ) : (
                    <>
                      <FiCheck className="text-lg" />
                      Create Holiday
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            💡 Holiday Creation Guidelines
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>• Use official holiday names that employees will easily recognize</li>
            <li>• Select accurate dates for each holiday</li>
            <li>• Provide meaningful descriptions to explain the holiday's significance</li>
            <li>• Common holidays include: National holidays, Religious festivals, Company-specific holidays</li>
            <li>• Holidays are automatically visible to all employees once created</li>
            <li>• You can edit holiday details later if needed</li>
            <li>• Fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateHoliday;